import React from 'react';
import { Pencil, Phone, Mail, FileText, Download, Link2 as LinkIcon } from 'lucide-react';
import type { Student } from '../../types';
import { titleCase } from '@/utils/text';

interface Props {
  student: Student;
  onEdit: () => void;
}

const ProfileTab: React.FC<Props> = ({ student, onEdit }) => {
  const contacts = student.contacts ?? [];
  const externalSiblings = student.externalSiblings ?? [];
  const enrolledSiblings = student.enrolledSiblings ?? [];
  const phoneLinkedSiblings = student.phoneLinkedSiblings ?? [];
  const docs = student.documents ?? [];
  const med = student.medicalInfo;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Personal */}
      <Section title="Personal details" action={<button className="btn btn-soft btn-sm" onClick={onEdit}><Pencil size={13} /> Edit</button>}>
        <dl style={grid}>
          <Field label="Full name" value={titleCase(student.display_name)} />
          <Field label="Admission no." value={student.admission_number} />
          <Field label="Roll no." value={student.roll_number} />
          <Field label="Class" value={student.class ? `${student.class.class_label}${student.section ? ` · ${student.section.section_label}` : ''}` : null} />
          <Field label="Date of birth" value={student.date_of_birth} />
          <Field label="Gender" value={student.gender} />
          <Field label="Blood group" value={student.blood_group} />
          <Field label="Category" value={student.category} />
          <Field label="Religion" value={student.religion} />
          <Field label="Nationality" value={student.nationality} />
          <Field label="Joining date" value={student.joining_date} />
        </dl>
      </Section>

      {/* Guardians / contacts */}
      <Section title={`Guardians & contacts (${contacts.length})`}>
        {contacts.length === 0 ? (
          <Muted text="No contacts added." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {contacts.map((c) => (
              <div key={c.id} className="card card-pad" style={{ padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700 }}>{titleCase(c.full_name)}</span>
                  <span className="chip chip-sm">{titleCase(c.relation_type)}</span>
                  {c.is_primary_guardian && <span className="badge b-info"><span className="dot" />Primary</span>}
                  {c.is_emergency_contact && <span className="badge b-warning"><span className="dot" />Emergency</span>}
                </div>
                <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 13, color: 'var(--text-2)', flexWrap: 'wrap' }}>
                  {c.phone_primary && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Phone size={13} /> {c.phone_primary}</span>}
                  {c.email && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Mail size={13} /> {c.email}</span>}
                  {c.occupation && <span>{c.occupation}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Siblings */}
      <Section title="Siblings">
        {enrolledSiblings.length === 0 && externalSiblings.length === 0 && phoneLinkedSiblings.length === 0 ? (
          <Muted text="No siblings recorded." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(enrolledSiblings.length > 0 || externalSiblings.length > 0) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {enrolledSiblings.map((s) => (
                  <span key={`e${s.relationship_id}`} className="chip">
                    {titleCase(s.sibling.display_name)} <span style={{ color: 'var(--text-3)' }}>· {titleCase(s.relation_type)} · enrolled</span>
                  </span>
                ))}
                {externalSiblings.map((s) => (
                  <span key={`x${s.id}`} className="chip">
                    {titleCase(s.sibling_name)}
                    <span style={{ color: 'var(--text-3)' }}>{s.school_name ? ` · ${titleCase(s.school_name)}` : ' · external'}</span>
                  </span>
                ))}
              </div>
            )}

            {phoneLinkedSiblings.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>
                  <LinkIcon size={12} /> Detected via shared guardian phone
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {phoneLinkedSiblings.map((s) => (
                    <span key={`p${s.id}`} className="chip">
                      {titleCase(s.display_name)}
                      <span style={{ color: 'var(--text-3)' }}>
                        {s.class_label ? ` · ${s.class_label}${s.section_label ? ` ${s.section_label}` : ''}` : ''}
                        {s.shared_phone ? ` · ${s.shared_phone}` : ''}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Medical */}
      <Section title="Medical information">
        {!med || (!med.allergies && !med.medical_conditions && !med.medications && !med.doctor_name && !med.notes) ? (
          <Muted text="No medical information recorded." />
        ) : (
          <dl style={grid}>
            <Field label="Allergies" value={med.allergies} />
            <Field label="Conditions" value={med.medical_conditions} />
            <Field label="Medications" value={med.medications} />
            <Field label="Doctor" value={med.doctor_name ? titleCase(med.doctor_name) : null} />
            <Field label="Doctor phone" value={med.doctor_phone} />
            <Field label="Notes" value={med.notes} />
          </dl>
        )}
      </Section>

      {/* Documents */}
      <Section title={`Documents (${docs.length})`}>
        {docs.length === 0 ? (
          <Muted text="No documents uploaded." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {docs.map((d) => (
              <a key={d.id} href={d.file_url} target="_blank" rel="noreferrer"
                 className="card" style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: 'inherit' }}>
                <FileText size={16} style={{ color: 'var(--text-2)' }} />
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>{d.title}</span>
                <span className="chip chip-sm">{titleCase(d.doc_type.replace('_', ' '))}</span>
                <Download size={15} style={{ marginLeft: 'auto', color: 'var(--text-3)' }} />
              </a>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, letterSpacing: '-0.2px' }}>{title}</h2>
        {action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</dt>
      <dd style={{ margin: '3px 0 0', fontSize: 14, color: value ? 'var(--text)' : 'var(--text-3)', textTransform: label === 'Gender' ? 'capitalize' : 'none' }}>
        {value || '—'}
      </dd>
    </div>
  );
}

function Muted({ text }: { text: string }) {
  return <div style={{ fontSize: 13, color: 'var(--text-3)' }}>{text}</div>;
}

const grid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  gap: 16, margin: 0,
};

export default ProfileTab;
